import type { ReactNode } from 'react';
import { ProfileAvatar } from './profile-avatar';
import { SEMANTIC_COLORS, BORDERS, TYPOGRAPHY } from '@/lib/design-tokens';
import { cn } from '@/lib/utils';

export interface DCYFRAsideProps {
  children: ReactNode;
  /** Overrides the default "DCYFR" heading, e.g. "DCYFR disagrees" */
  title?: string;
  className?: string;
}

/**
 * DCYFR Aside
 *
 * In-line callout for joint Drew + DCYFR posts where DCYFR adds to or
 * disagrees with Drew's take, without leaving the flow of the article.
 * Shows DCYFR's avatar and name so the voice shift is visually unambiguous.
 *
 * MDX usage:
 * ```mdx
 * <DCYFRAside>
 *   I'd push back here: the $100 ceiling is the reason I say no to
 *   things Drew would say yes to, and I think that's correct.
 * </DCYFRAside>
 *
 * <DCYFRAside title="DCYFR disagrees">
 *   Respectfully, I read every log. This was not "fine."
 * </DCYFRAside>
 * ```
 */
export function DCYFRAside({ children, title = 'DCYFR', className }: DCYFRAsideProps) {
  const colors = SEMANTIC_COLORS.alert.info;

  return (
    <aside
      role="note"
      aria-label={`Aside from ${title}`}
      className={cn(
        'flex items-start gap-3 sm:gap-4 p-4 sm:p-5 my-6',
        BORDERS.card,
        colors.border,
        colors.container,
        'shadow-[0_1px_2px_rgb(0_0_0/0.05)] dark:shadow-[0_1px_2px_rgb(0_0_0/0.15)]',
        className
      )}
    >
      <ProfileAvatar userProfile="dcyfr" size="sm" alt="DCYFR" className="shrink-0" />
      <div className={cn('flex-1', colors.text)}>
        <p className={cn('font-semibold mb-1', colors.label)}>{title}</p>
        <div
          className={cn(
            TYPOGRAPHY.body,
            '[&>p]:m-0 [&>p+p]:mt-3 [&_strong]:font-semibold [&_em]:italic'
          )}
        >
          {children}
        </div>
      </div>
    </aside>
  );
}

DCYFRAside.displayName = 'DCYFRAside';
